'use client';

import React from 'react';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { Avatar, Box, Skeleton, Typography } from '@mui/material';

import { useAuth, useT } from '@/hooks';
import { useStreakLeaderboard } from '@/services/query/streak.queries';
import { colors } from '@/styles/theme';
import type { StreakLeaderboardEntry } from '@/types/streak.types';
// Deep import, not the dashboard barrel — see StreakHistorySection.tsx: that
// barrel re-exports three recharts-importing cards.
import { DashboardCard } from '@/views/Portfolio/components/dashboard/DashboardCard';

const RANK_COLOR = ['#f5b301', colors.neutral[400], '#cd7f32']; // gold / silver / bronze

/**
 * StreakLeaderboardSection
 * Full-width take on the streak leaderboard — top 9 live streaks, plus a
 * divided "you" row when the current user isn't already in the top 9.
 */
export const StreakLeaderboardSection: React.FC = () => {
  const { t } = useT();
  const { isAuthenticated } = useAuth();
  const { data, isLoading } = useStreakLeaderboard(isAuthenticated);

  const top = data?.top ?? [];
  const self = data?.self ?? null;
  const selfInTop = self != null && top.some((entry) => entry.appUserId === self.appUserId);
  const showSelfRow = Boolean(self && !selfInTop);

  return (
    <DashboardCard
      title={t('streaks.leaderboardTitle', { defaultValue: 'Streak leaderboard' })}
      subtitle={t('streaks.leaderboardSubtitle', {
        defaultValue: "Ranked by who's on the longest active run right now",
      })}
      icon={<LocalFireDepartmentIcon sx={{ fontSize: 20, color: '#e8b046' }} />}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Skeleton variant="text" width="35%" />
              <Skeleton variant="text" width={40} sx={{ ml: 'auto' }} />
            </Box>
          ))}
        </Box>
      ) : top.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {t('streaks.leaderboardEmpty', {
              defaultValue: 'No active streaks yet — be the first.',
            })}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: '2px 24px',
          }}
        >
          {top.map((entry) => (
            <LeaderboardRow
              key={entry.appUserId}
              entry={entry}
              isMe={self != null && self.appUserId === entry.appUserId}
            />
          ))}
          {showSelfRow && self && (
            <>
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  borderTop: '1px dashed',
                  borderColor: 'divider',
                  my: 0.5,
                }}
              />
              <LeaderboardRow entry={self} isMe />
            </>
          )}
        </Box>
      )}
    </DashboardCard>
  );
};

const LeaderboardRow: React.FC<{ entry: StreakLeaderboardEntry; isMe: boolean }> = ({
  entry,
  isMe,
}) => {
  const { t } = useT();
  const name = entry.displayName?.trim() || `Trader #${entry.appUserId}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isMe ? 'primary.main' : 'transparent',
        bgcolor: isMe ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
        transition: 'background-color 0.2s ease',
        '&:hover': { bgcolor: isMe ? 'rgba(37, 99, 235, 0.1)' : 'action.hover' },
      }}
    >
      <Box
        sx={{
          width: 24,
          textAlign: 'center',
          fontWeight: 800,
          fontSize: '0.9rem',
          color: entry.rank <= 3 ? RANK_COLOR[entry.rank - 1] : 'text.secondary',
          flexShrink: 0,
        }}
      >
        {entry.rank}
      </Box>
      <Avatar sx={{ width: 36, height: 36, fontSize: '0.9rem', fontWeight: 700 }}>
        {name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
          {name}
          {isMe && (
            <Box
              component="span"
              sx={{
                ml: 0.75,
                px: 0.75,
                py: 0.15,
                borderRadius: 1,
                fontSize: '0.6rem',
                fontWeight: 800,
                color: 'primary.main',
                bgcolor: 'rgba(37, 99, 235, 0.12)',
              }}
            >
              {t('streaks.you', { defaultValue: 'YOU' })}
            </Box>
          )}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <LocalFireDepartmentIcon sx={{ fontSize: 16, color: '#e8b046' }} />
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'text.primary' }}>
          {entry.consecutiveDays}d
        </Typography>
      </Box>
    </Box>
  );
};
